import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQuery, createApiErrorTransformer } from '@/lib/api/baseQuery';
import {
  GetPortfolioCombinedSummaryParams,
  GetPortfolioCombinedSummaryResponse,
  SchemeMasterParams,
  SchemeMasterResponse,
  BSESchemeMasterResponse,
  SipStatusParams,
  SipStatusResponse,
  MandateParams,
  MandateResponse,
  TransactionsParams,
  TransactionsResponse,
  SchemeFilterResponse,
  TopPerformingSchemesResponse,
  TopPerformingSchemesParams,
  GetPortfolioCombinedSummaryAPResponse,
  GetPortfolioCombinedSummaryAPParams,
  MonthlyPerformanceResponse,
  MonthlyPerformanceParams,
  FactsheetResponse,
  NavSeriesResponse,
  NavSeriesParams,
  SchemeMasterSearchResponse,
  SchemeMasterSearchParams,
  LumpsumNavResponse,
  LumpsumNavParams,
  SipNavResponse,
  SipNavParams,
} from '@/types/wealth/mutualFund';

import { StorageUtils } from '@/lib/utils/encryption';
const MUTUAL_FUND_API_URL = import.meta.env.VITE_APP_MUTUALFUNDS_URL;

// RTK Query service for Mutual Fund feature
export const mutualFundApi = createApi({
  reducerPath: 'mutualFundApi',
  baseQuery: createBaseQuery(MUTUAL_FUND_API_URL),
  tagTypes: [
    'MutualFundPortfolio',
    'MutualFundExplore',
    'MutualFundSummary',
    'Factsheet',
    'NavSeries',
    'SchemeMaster',
    'MultipleBenchmarkDataList',
    'MultipleBenchmarkData',
    'LumpsumNav',
    'SipNav',
  ],
  endpoints: (builder) => ({
    // SIP Status - renamed and simplified for SIP tracking
    getSipStatus: builder.query<SipStatusResponse, SipStatusParams>({
      query: ({ clientCode, sxpType = 'sip', page = 1, limit = 10, source = 'ap' }) => ({
        url: `/api/v2/bse-v2/sxp-client-wise`,
        params: { clientCode, sxpType, page, limit, source },
      }),
      providesTags: (_res, _err, _arg) => ['MutualFundPortfolio'],
      transformErrorResponse: createApiErrorTransformer('MutualFundSipStatus', true),
    }),

    // Mandate - Client wise
    getMandateClientWise: builder.query<MandateResponse, MandateParams>({
      query: ({ clientCode, page = 1, limit = 10, search, source = 'ap' }) => ({
        url: `/api/v2/bse-v2/mandate-client-wise`,
        params: { clientCode, page, limit, search, source },
      }),
      providesTags: (_res, _err, _arg) => ['MutualFundPortfolio'],
      transformErrorResponse: createApiErrorTransformer('MutualFundMandateClientWise', true),
    }),

    getSchemeMaster: builder.query<BSESchemeMasterResponse, SchemeMasterParams>({
      query: ({
        searchTerm = '',
        pageNumber = 1,
        pageSize = 10,
        amcCode,
        category,
        riskProfile,
        schemeType,
        investmentType,
        source = 'ap',
        peerCompare = false,
        schemeCode,
      } = {}) => ({
        url: `/api/v2/scheme/scheme-details`,
        // url: `/api/v2/scheme/bse-scheme-master`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          source,
          ...(searchTerm && searchTerm.trim() !== '' && { searchTerm: searchTerm.trim() }),
          ...(category && category.length > 0 && { category: category.join(',') }),
          ...(riskProfile && riskProfile.length > 0 && { riskProfile: riskProfile.join(',') }),
          // ...(fundSize && fundSize.length > 0 && { fundSize: fundSize.join(',') }),
          ...(investmentType &&
            investmentType.length > 0 && { investmentType: investmentType.join(',') }),
          ...(schemeType && schemeType.length > 0 && { schemeType: schemeType.join(',') }),
          ...(amcCode && amcCode.length > 0 && { amcCode: amcCode }),
          ...(peerCompare && { peerCompare: peerCompare }),
          ...(schemeCode && { schemeCode: schemeCode }),
        },
      }),
      providesTags: (_res, _err, _arg) => ['MutualFundExplore'],
      transformErrorResponse: createApiErrorTransformer('MutualFundSchemeMaster', true),
    }),

    // Portfolio Combined Summary by PAN
    getPortfolioCombinedSummary: builder.query<
      GetPortfolioCombinedSummaryResponse,
      GetPortfolioCombinedSummaryParams | void
    >({
      query: (args) => {
        return {
          url: `/api/v2/report/get-portfolio`,
          params: {
            source: 'ap',
            ...(args?.clientCode ? { clientCode: args.clientCode } : {}),
          },
        };
      },
      providesTags: ['MutualFundSummary'],
      transformErrorResponse: createApiErrorTransformer('MutualFundPortfolioCombinedSummary', true),
    }),

    getPortfolioCombinedSummaryAP: builder.query<
      GetPortfolioCombinedSummaryAPResponse,
      GetPortfolioCombinedSummaryAPParams | void
    >({
      query: (args) => {
        return {
          // url: `/api/v2/mf/get-mf-portfolio-summary`,
          url: '/api/v2/user/branch-gain-summary',
          params: {
            source: 'ap',
          },
        };
      },
      providesTags: ['MutualFundSummary'],
      transformErrorResponse: createApiErrorTransformer('MutualFundPortfolioCombinedSummary', true),
    }),

    // Transactions by PAN and date range
    getTransactions: builder.query<TransactionsResponse, TransactionsParams | void>({
      query: (args) => {
        const pan = args?.pan ?? undefined;
        // default a reasonably wide range if not provided
        const start_date = args?.start_date ?? '2020-01-01';
        const end_date = args?.end_date ?? new Date().toISOString().slice(0, 10);
        const params: Record<string, any> = { pan, start_date, end_date, source: 'ap' };
        if (args?.page_number) params.page_number = args.page_number;
        if (args?.page_size) params.page_size = args.page_size;
        if (args?.client_code) params.client_code = args.client_code;
        return {
          url: `/api/v2/report/get-transactions`,
          params,
        };
      },
      providesTags: (_res, _err, _arg) => ['MutualFundPortfolio'],
      transformErrorResponse: createApiErrorTransformer('MutualFundTransactions', true),
    }),

    // Scheme Filters - Get dropdown options for filters
    getSchemeFilter: builder.query<SchemeFilterResponse, void>({
      query: () => ({
        url: `/api/v2/scheme/get-scheme-filter`,
        method: 'GET',
        params: { source: 'ap' },
      }),
      providesTags: ['MutualFundExplore'],
      transformErrorResponse: createApiErrorTransformer('MutualFundSchemeFilter', true),
    }),

    // Top Performing Schemes
    getTopPerformingSchemes: builder.query<
      TopPerformingSchemesResponse,
      TopPerformingSchemesParams
    >({
      query: ({
        scheme_type = 'EQUITY',
        return_period = 'all_time_return',
        limit = 10,
        page = 1,
        source = 'ap',
        clientCode,
      } = {}) => ({
        url: `/api/v2/scheme/top-performing`,
        params: {
          scheme_type,
          return_period,
          limit,
          page,
          source,
          ...(clientCode ? { clientCode } : {}),
        },
      }),
      providesTags: ['MutualFundExplore'],
      transformErrorResponse: createApiErrorTransformer('TopPerformingSchemes', true),
    }),

    // Portfolio Overview - Get XIRR and performance metrics
    getPortfolioOverview: builder.query<
      any,
      { year?: string | number; source?: string; clientCode?: string; pan?: string }
    >({
      query: (args = {}) => {
        const {
          year = 'all',
          source = 'ap',
          clientCode,
          pan,
        } = args as {
          year?: string | number;
          source?: string;
          clientCode?: string;
          pan?: string;
        };
        return {
          url: `/api/v2/user/get-portfolio-overview`,
          params: { year, source, ...(clientCode ? { clientCode } : {}), ...(pan ? { pan } : {}) },
        };
      },
      providesTags: ['MutualFundSummary'],
      transformErrorResponse: createApiErrorTransformer('PortfolioOverview', true),
    }),

    // Benchmark Data - Get benchmark XIRR
    getBenchmarkData: builder.query<
      any,
      {
        benchmarkCode: string;
        source?: string;
        year?: number;
        segment?: number;
        clientCode?: string;
      }
    >({
      query: ({
        benchmarkCode,
        source = 'ap',
        year = new Date().getFullYear(),
        segment = 1,
        clientCode,
      }) => ({
        url: `/api/v2/admin/benchmark/data`,
        params: { benchmarkCode, source, year, segment, ...(clientCode ? { clientCode } : {}) },
      }),
      transformErrorResponse: createApiErrorTransformer('BenchmarkData', true),
    }),

    getMonthlyPerformanceData: builder.query<MonthlyPerformanceResponse, MonthlyPerformanceParams>({
      query: ({
        source = 'ap',
        // year = new Date().getFullYear(),
        clientCode,
      }) => ({
        url: `/api/v2/report/monthly-portfolio-summary`,
        params: { source, ...(clientCode ? { clientCode } : {}) },
      }),
      transformErrorResponse: createApiErrorTransformer('MonthlyPerformanceData', true),
    }),

    getFactsheet: builder.query<FactsheetResponse, { scheme_code: string; scheme_isin: any }>({
      query: ({ scheme_code, scheme_isin }) => ({
        url: '/api/v2/scheme/factsheet',
        method: 'GET',
        params: { scheme_code, scheme_isin, source: 'ap' },
      }),
      providesTags: ['Factsheet'],
      transformErrorResponse: createApiErrorTransformer('SchemeFactsheet', true),
    }),

    getNavSeries: builder.query<NavSeriesResponse, NavSeriesParams>({
      query: ({ scheme_code, time_period }) => ({
        url: '/api/v2/scheme/get-nav-data',
        method: 'GET',
        params: { scheme_code, time_period, source: 'ap' },
      }),
      providesTags: ['NavSeries'],
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('SchemeNavSeries', true),
    }),

    // searchSchemeMaster: builder.query<SchemeMasterSearchResponse, SchemeMasterSearchParams>({
    //   query: ({
    //     page,
    //     pageSize,
    //     search,
    //     amc_index,
    //     category,
    //     fund_size,
    //     invest_type,
    //     risk,
    //     scheme_option,
    //     stp_in_flag,
    //   }) => ({
    //     // url: '/api/v2/scheme/bse-scheme-master',
    //     url: '/api/v2/scheme/scheme-details',
    //     method: 'GET',
    //     params: {
    //       page,
    //       limit: pageSize,
    //       source: 'ap',
    //       ...(amc_index ? { amc_index } : {}),
    //       ...(category ? { category } : {}),
    //       ...(fund_size ? { fund_size } : {}),
    //       ...(invest_type ? { invest_type } : {}),
    //       ...(risk ? { risk } : {}),
    //       ...(scheme_option ? { scheme_option } : {}),
    //       ...(search ? { search } : {}),
    //       ...(stp_in_flag !== undefined ? { stp_in_flag } : {}),
    //     },
    //   }),
    //   providesTags: (result) =>
    //     result?.data?.data
    //       ? [
    //           ...result.data.data.map((item: any) => ({
    //             type: 'SchemeMaster' as const,
    //             id:
    //               item?.bse_scheme_details?.scheme_amfi_code ||
    //               item?.scheme_code ||
    //               item?.id ||
    //               'UNKNOWN',
    //           })),
    //           { type: 'SchemeMaster', id: 'LIST' },
    //         ]
    //       : [{ type: 'SchemeMaster', id: 'LIST' }],
    //   // Keep API response shape; page code already expects data.data
    //   transformResponse: (response: any) => response,
    //   transformErrorResponse: createApiErrorTransformer('SchemeMasterSearch', true),
    // }),

    getMultipleBenchmarkDataList: builder.query<any, any>({
      query: () => ({
        url: '/api/v2/admin/benchmark/list',
        method: 'GET',
        params: {
          source: 'ap',
        },
      }),
      // Use proper signature: (result, error, arg)
      providesTags: ['MultipleBenchmarkDataList'],
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: createApiErrorTransformer('SchemeMultipleBenchmarkDataList', true),
    }),

    getMultipleBenchmarkData: builder.query<any, any>({
      query: (body) => ({
        url: '/api/v2/admin/benchmark/multiple-data',
        method: 'POST',
        params: {
          source: 'ap',
        },
        body,
      }),
      // Use proper signature: (result, error, arg)
      providesTags: ['MultipleBenchmarkData'],
      transformResponse: (response: any) => response?.data,
      transformErrorResponse: createApiErrorTransformer('SchemeMultipleBenchmarkData', false),
    }),

    getLumpsumNavData: builder.query<LumpsumNavResponse, LumpsumNavParams>({
      query: ({ scheme_code, time_period, lumpsum_amount }) => ({
        url: '/api/v2/scheme/get-nav-lumpsum',
        method: 'GET',
        params: { scheme_code, time_period, lumpsum_amount, source: 'ap' },
      }),
      providesTags: (result, error, { scheme_code, time_period, lumpsum_amount }) => [
        { type: 'LumpsumNav', id: `${scheme_code}-${time_period}-${lumpsum_amount}` },
      ],
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('SchemeLumpsumNav', true),
    }),

    getSipNavData: builder.query<SipNavResponse, SipNavParams>({
      query: ({ scheme_code, time_period, sip_amount }) => ({
        url: '/api/v2/scheme/get-nav-sip',
        method: 'GET',
        params: { scheme_code, time_period, sip_amount, source: 'ap' },
      }),
      providesTags: (result, error, { scheme_code, time_period, sip_amount }) => [
        { type: 'SipNav', id: `${scheme_code}-${time_period}-${sip_amount}` },
      ],
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('SchemeSipNav', true),
    }),
  }),
});

export const {
  useGetSipStatusQuery,
  useGetSchemeMasterQuery,
  useGetPortfolioCombinedSummaryQuery,
  useGetPortfolioCombinedSummaryAPQuery,
  useGetMandateClientWiseQuery,
  useGetTransactionsQuery,
  useGetSchemeFilterQuery,
  useGetTopPerformingSchemesQuery,
  useGetPortfolioOverviewQuery,
  useGetBenchmarkDataQuery,
  useGetMonthlyPerformanceDataQuery,

  useGetFactsheetQuery,
  useLazyGetFactsheetQuery,
  useGetNavSeriesQuery,
  useLazyGetNavSeriesQuery,
  // useSearchSchemeMasterQuery,
  // useLazySearchSchemeMasterQuery,
  useGetMultipleBenchmarkDataListQuery,
  useGetMultipleBenchmarkDataQuery,
  useGetLumpsumNavDataQuery,
  useLazyGetLumpsumNavDataQuery,
  useGetSipNavDataQuery,
  useLazyGetSipNavDataQuery,
} = mutualFundApi;
